import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SectionHeader from "@/components/site/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Send } from "lucide-react";
import { api } from "@/lib/api";
import { FormNotice } from "@/components/site/Notices";
import ConsentCheckbox from "@/components/site/ConsentCheckbox";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema } from "@/components/seo/schema";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [consent, setConsent] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of Object.keys(EMPTY)) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please complete every field. Use N/A or Unknown where it does not apply.");
        return;
      }
    }
    if (!consent) {
      toast.error("Please review and agree to the Terms & Conditions to continue.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", { ...form, consent_agreement: true });
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact Equinox Systems"
        description="Contact Equinox Systems about the Free Custom Website Program, custom websites, apps, CRMs, and AI integrations. Nationwide remote web development."
        path="/contact"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 eq-grid-bg" />
        <div className="eq-aurora" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeader
              as="h1"
              eyebrow="Contact"
              title="Let's build something that grows your business."
              description="Send us a message and we'll get back within 1 business day."
            />
            <div className="space-y-4 text-sm text-gray-300">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-400" />
                <a href="mailto:Equinox@freedomcoreventures.com" data-testid="contact-email-link" className="hover:text-white">
                  Equinox@freedomcoreventures.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-blue-400" />
                <span>equinoxsystems.tech · United States</span>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-6"><FormNotice /></div>
            <form onSubmit={submit} data-testid="contact-form" className="glass rounded-3xl p-8 space-y-5">
              <div><Label className="text-gray-300 text-sm">Name *</Label><Input data-testid="contact-name" value={form.name} onChange={setField("name")} placeholder="Your name" className="mt-2 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-gray-300 text-sm">Email *</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={setField("email")} placeholder="you@business.com" className="mt-2 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-gray-300 text-sm">Subject *</Label><Input data-testid="contact-subject" value={form.subject} onChange={setField("subject")} placeholder="What's this about?" className="mt-2 bg-white/5 border-white/10 text-white" /></div>
              <div><Label className="text-gray-300 text-sm">Message *</Label><Textarea data-testid="contact-message" value={form.message} onChange={setField("message")} rows={5} placeholder="Tell us about your project..." className="mt-2 bg-white/5 border-white/10 text-white" /></div>
              <ConsentCheckbox checked={consent} onChange={setConsent} testid="contact-consent" />
              <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="contact-submit-btn">
                {submitting ? "Sending..." : "Send Message"} <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
